import { useState, type FormEvent } from 'react'
import { Box, TextField, Button, Typography, Alert, Link as MuiLink } from '@mui/material'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { DASHBOARD_AUTH_PATHS } from '@yourownmetric/shared'

import { needsMfa, useLoginMutation } from '@/apis/authApi'
import { useAppDispatch } from '@/app/hooks'

import { setCredentials } from './authSlice'
import { GoogleSignInButton } from './GoogleSignInButton'
import { safeNextPath } from './nextPath'
import { MfaChallengeForm } from './MfaChallengeForm'

export function LoginPage() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [login, { isLoading, error }] = useLoginMutation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [params] = useSearchParams()
    const next = safeNextPath(params.get('next'))
    const [challenge, setChallenge] = useState<string | null>(null)

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            const result = await login({ email, password }).unwrap()
            if (needsMfa(result)) {
                setChallenge(result.mfaChallengeToken)
                return
            }
            dispatch(setCredentials(result))
            navigate(next)
        } catch {
            // error handled by RTK Query
        }
    }

    if (challenge) {
        return (
            <MfaChallengeForm
                challengeToken={challenge}
                onDone={() => navigate(next)}
                onRestart={() => {
                    setChallenge(null)
                    setPassword('')
                }}
            />
        )
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400, mx: 'auto' }}
        >
            <Typography variant="h4" textAlign="center">
                Sign In
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
                Welcome back to Your Own Metric
            </Typography>

            {error && <Alert severity="error">Invalid email or password</Alert>}

            <TextField
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                fullWidth
            />
            <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                fullWidth
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <MuiLink component={Link} to={DASHBOARD_AUTH_PATHS.forgotPassword} variant="body2">
                    Forgot password?
                </MuiLink>
            </Box>

            <Button type="submit" variant="contained" size="large" disabled={isLoading} fullWidth>
                {isLoading ? 'Signing in…' : 'Sign In'}
            </Button>

            <GoogleSignInButton text="signin_with" next={next} onMfaRequired={setChallenge} />

            <Typography variant="body2" textAlign="center">
                Don&apos;t have an account?{' '}
                <MuiLink
                    component={Link}
                    to={
                        params.get('next')
                            ? `${DASHBOARD_AUTH_PATHS.signup}?next=${encodeURIComponent(next)}`
                            : DASHBOARD_AUTH_PATHS.signup
                    }
                >
                    Create one
                </MuiLink>
            </Typography>
        </Box>
    )
}
